import React from "react";
import Header3 from "../components/Header3";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";

const Privacy = () => {
  return (
    <>
      <div className=" d-flex flex-column" >
        <Header3 />
        <div className="min-h-screen bg-[#F7F7F7] text-neutral-900">
          {/* Top Bar */}
          <header className="sticky top-0 z-40 border-b border-neutral-200 bg-white/80 backdrop-blur">
            <div className="mx-auto flex max-w-6xl items-center justify-start px-4 py-4">
              <h1 className="text-lg font-semibold leading-tight">Privacy Policy</h1>
            </div>
          </header>

          {/* Content */}
          <main className="mx-auto max-w-3xl px-4 py-10">
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm md:p-8">
              <p className="mb-6 text-neutral-700"><strong>Effective date and last updated:</strong> 19 October 2025</p>
              <p className="mb-4 text-neutral-800"><strong>Introduction:</strong> This Privacy Policy (“Policy”) describes how Ofellio Limited (“Ofellio” “we,” “our,” or “us”) collects, uses, stores, and protects personal data when you visit or use our website www.ofellio.com, its subdomains, and related services (collectively, the “Platform”).</p>
              <p className="mb-4 text-neutral-800"><strong>Consent:</strong> By accepting our <a href="/terms" className="text-[#34C3C9] underline hover:text-[#0f6f73]">Terms of Use</a> and this Policy you acknowledge that you have read and understood this Policy and you consent to the collection, use, disclosure, storage and processing of your information in accordance with this Policy. If you do not agree with this Policy, please discontinue use of the Platform.</p>
              <div className="mb-4 text-neutral-800">
                <p><strong>Scope and Applicability:</strong> This Policy applies to all personal data collected from:</p>
                <ul className="ml-5 mt-2 list-disc space-y-1 text-neutral-800">
                  <li>Registered users of the Platform, including event participants and ambassadors;</li>
                  <li>Visitors who browse the Platform without registering; and</li>
                  <li>Any other individual who interacts with Ofellio through the Platform or related services.</li>
                </ul>
              </div>
              <div className="mb-4 text-neutral-800">
                <p><strong>Information you provide:</strong> When registering for an account or using our services, you may provide:</p>
                <ul className="ml-5 mt-2 list-disc space-y-1">
                  <li>Identification and contact data: name, email address, password;</li>
                  <li>Optional profile data: gender, age range, country or city, social media links, description, and profile photo;</li>
                  <li>Account role information: whether you register as a user or ambassador;</li>
                  <li>Content Data: text, photos, or other materials you upload or share as part of events or profiles;</li>
                  <li>Communication Data: messages, reviews, or feedback you post or send through the Platform.</li>
                </ul>
              </div>
              <div className="mb-4 text-neutral-800">
                <p><strong>Data we may collect from you:</strong> We collect personal data directly from you when:</p>
                <ul className="ml-5 mt-2 list-disc space-y-1">
                  <li>You create an account;</li>
                  <li>Use the Platform;</li>
                  <li>Attend an event;</li>
                  <li>Visit the Platform;</li>
                  <li>Communicate with us.</li>
                </ul>
              </div>
              <div className="mb-4 text-neutral-800">
                <p><strong>Data we may collect automatically:</strong> When you use the Platform, we also automatically collect certain information, including, but not limited to:</p>
                <ul className="ml-5 mt-2 list-disc space-y-1">
                  <li>Log Data: IP address, browser type, device type, operating system, access times, pages viewed, your mobile carrier, device information, search terms and referring URLs (even if you do not create an Ofellio’s account),</li>
                  <li>Usage Data: interactions with events, searches, and preferences;</li>
                  <li>Location Data: approximate geolocation derived from IP address;</li>
                  <li>Analytics Data: via cookies, Google Analytics, and similar technologies; and</li>
                  <li>Advertising Data: information collected via Meta Pixel and other tracking tools, where permitted. You can learn more about the types of cookies we and our service providers use by reading our <a href="/cookies" className="text-[#34C3C9] underline hover:text-[#0f6f73]">Cookie Policy</a>.</li>
                </ul>
              </div>
              <div className="mb-4 text-neutral-800">
                <p><strong>Data we may collect from third party sources:</strong> We may receive limited data from:</p>
                <ul className="ml-5 mt-2 list-disc space-y-1">
                  <li>Publicly available book information databases and APIs (e.g., national libraries or metadata providers);</li>
                  <li>Analytics or advertising partners; and</li>
                  <li>Social media platforms, if you connect or interact with our pages or posts.</li>
                </ul>
                <p className="mt-2">You may also register or log in through social login providers such as Google, Apple, or Facebook. When you do so, we receive limited account information from these providers (such as name, email, and profile photo) in accordance with their respective privacy policies.</p>
              </div>
              <div className="mb-4 text-neutral-800">
                <p><strong>How we use your data:</strong> We process your personal data only for lawful purposes, including, but not limited to,</p>
                <ul className="ml-5 mt-2 list-disc space-y-1">
                  <li>To create and manage your account and provide access to Platform features;</li>
                  <li>To enable participation and creation of book events (individual or group);</li>
                  <li>To authenticate your login and maintain account security;</li>
                  <li>To communicate with you, including service announcements or policy updates;</li>
                  <li>To analyze Platform activity and improve user experience;</li>
                  <li>To prevent fraud, abuse, and misuse of the Platform;</li>
                  <li>To comply with legal obligations and enforce our <a href="/terms" className="text-[#34C3C9] underline hover:text-[#0f6f73]">Terms of Use</a>;</li>
                  <li>With your consent, to send marketing communications or newsletters in the future;</li>
                  <li>To personalize content and recommendations based on your activities and preferences; and</li>
                  <li>To other business and advertisement purposes, such as providing content, features, or sponsorships that match member profiles or interests, delivering more relevant advertising, and facilitating contests and other promotions.</li>
                </ul>
              </div>
              <p className="mb-4 text-neutral-800"><strong>Sharing and disclosure of data:</strong> We do not sell personal data. We may share it only as strictly necessary with service providers, API providers, legal authorities, or with your explicit consent.</p>
              <p className="mb-4 text-neutral-800"><strong>Legal basis and international data transfers:</strong> Ofellio is based in the EU and operates globally. Where personal data is transferred outside the EEA, Ofellio ensures adequate safeguards consistent with applicable law.</p>
              <p className="mb-4 text-neutral-800"><strong>Data Retention:</strong> We retain personal data only as long as necessary for the purposes described and applicable law.</p>
              <p className="mb-4 text-neutral-800"><strong>Security:</strong> We implement technical and organizational measures to safeguard personal data but cannot guarantee absolute security. Keep your password secure and confidential.</p>
              <p className="mb-4 text-neutral-800"><strong>Your rights:</strong> You may have rights to access, rectify, delete, restrict, object, or transfer your personal data and to withdraw consent or file a complaint with a supervisory authority.</p>
              <p className="mb-4 text-neutral-800"><strong>Age restriction:</strong> The Platform is intended only for users aged 18 years or older.</p>
              <p className="mb-4 text-neutral-800"><strong>Links to third parties:</strong> The Platform may contain links to external websites. Ofellio is not responsible for their privacy practices.</p>
              <p className="mb-4 text-neutral-800"><strong>Changes to this policy:</strong> Ofellio may update this Policy periodically. Continued use of the Platform constitutes acceptance of updates.</p>
              <div className="text-neutral-800">
                <p><strong>Contact and data protection queries:</strong> For questions, concerns, or data access requests, please visit the Help Page on www.ofellio.com.</p>
              </div>
            </div>
           
          </main>{/* Footer */}
 <footer className="bg-[#F2F2F2] border-t border-neutral-200">
              <div className="mx-auto flex max-w-6xl flex-col justify-between gap-6 px-6 py-8 md:flex-row md:items-center">
                <div className="flex flex-col space-y-2 text-sm text-neutral-700">
                  <Link to="/terms" className="hover:text-[#34C3C9]">
                    Terms of use
                  </Link>
                  <Link to="/privacy" className="hover:text-[#34C3C9]">
                    Privacy Policy
                  </Link>
                  <Link to="/cookies" className="hover:text-[#34C3C9]">
                    Cookies Policy
                  </Link>
                  <Link to="/help" className="hover:text-[#34C3C9]">
                    Help
                  </Link>
                </div>
                <div className="flex items-center justify-center md:justify-end">
                  <img
                    src={require("../components/image/footerlogo.png")}
                    alt="Ofellio Logo"
                    className="h-8 w-auto"
                  />
                </div>
              </div>
            </footer>
        </div>

      </div>
    </>
  );
};

export default Privacy;
